import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isEmpty, get } from 'dpl/shared/utils/object';
import {
  sendAhoyAndGAEvents,
  sendAnalyticsIdentify,
  sendRudderstackEvent
} from 'dpl/util/analytics';
import postSignupRedirect from 'dpl/util/postSignupRedirect';
import { USER_TYPES } from 'dpl/shared/constants/shared';
import { TOS_PATH, PRIVACY_PATH } from 'dpl/shared/constants/urls';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  EVENT_CATEGORIES
} from 'dpl/constants/analytics';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import Input from 'dpl/common/design_system/Input';
import TelephoneInput from 'dpl/common/design_system/TelephoneInput';
import Button from 'dpl/common/design_system/Button';
import PasswordInput from 'dpl/common/design_system/PasswordInput';
import GenericSelect from 'dpl/common/design_system/GenericSelect';

export default function SignupForm({
  buttonClassName,
  buttonText,
  formState,
  handleFormFieldChange,
  isFormSubmitting,
  countriesData,
  userType,
  formErrors,
  selectedCountry,
  referredBy,
  handleFormSubmission,
  didFormSubmissionFail,
  onAuth,
  partnership
}) {
  function handleSubmit(e) {
    e.preventDefault();

    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: buttonText,
      view: ANALYTICS_VIEWS.CREATE_ACCOUNT_SCREEN,
      view_feature: ANALYTICS_VIEW_FEATURES.CONTINUE_BUTTON
    });

    handleFormSubmission({
      user: {
        referred_by: referredBy,
        partnership
      },
      userType
    }).then(json => {
      if (json.response.ok) {
        if (userType === USER_TYPES.BREEDER) {
          sendAhoyAndGAEvents('submit', {
            event_category: EVENT_CATEGORIES.BREEDER_SIGNUP
          });
        }

        sendAnalyticsIdentify({ userId: get(json, 'data.user.id') });

        if (onAuth) {
          return onAuth(json);
        }

        return postSignupRedirect(userType);
      }
      return null;
    });
  }

  const isBreeder = userType === USER_TYPES.BREEDER;

  const countriesOptions = countriesData.value.data.map(c => ({
    title: c.name,
    value: c.id
  }));

  return (
    <div className={classnames('SignupForm', { BreederSignupForm: isBreeder })}>
      <form onSubmit={handleSubmit}>
        <div className="row mb3">
          <div className="col">
            <label htmlFor="first_name">
              <p className="secondary font-12 mb2">First Name</p>
              <ErrorWrapper
                errors={formErrors.first_name}
                isShown={didFormSubmissionFail}
              >
                <Input
                  name="first_name"
                  className="w-100"
                  value={formState.first_name || ''}
                  onChange={handleFormFieldChange}
                  placeholder="Jane"
                />
              </ErrorWrapper>
            </label>
          </div>
          <div className="col">
            <label htmlFor="last_name">
              <p className="secondary font-12 mb2">Last Name</p>
              <ErrorWrapper
                errors={formErrors.last_name}
                isShown={didFormSubmissionFail}
              >
                <Input
                  name="last_name"
                  className="w-100"
                  value={formState.last_name || ''}
                  onChange={handleFormFieldChange}
                  placeholder="Smith"
                />
              </ErrorWrapper>
            </label>
          </div>
        </div>

        {isBreeder && (
          <Fragment>
            <div className="row mb3">
              <div className="col">
                <label htmlFor="program_name">
                  <p className="secondary font-12 mb2">Program Name</p>
                  <ErrorWrapper
                    errors={formErrors.program_name}
                    isShown={didFormSubmissionFail}
                  >
                    <Input
                      name="program_name"
                      className="w-100"
                      value={formState.program_name || ''}
                      onChange={handleFormFieldChange}
                      placeholder="e.g. High Peaks Farm"
                    />
                  </ErrorWrapper>
                </label>
              </div>
            </div>
            <div className="row mb3">
              <div className="col">
                <label htmlFor="country_id">
                  <p className="secondary font-12 mb2">Country</p>
                  <ErrorWrapper
                    errors={formErrors.country_id}
                    isShown={didFormSubmissionFail}
                  >
                    <GenericSelect
                      name="country_id"
                      onChange={handleFormFieldChange}
                      options={countriesOptions}
                      value={formState.country_id}
                    />
                  </ErrorWrapper>
                </label>
              </div>
            </div>
          </Fragment>
        )}
        <div className="mb3">
          <label htmlFor="email">
            <p className="secondary font-12 mb2">Email</p>
            <ErrorWrapper
              errors={formErrors.email}
              isShown={didFormSubmissionFail}
            >
              <Input
                name="email"
                autoCapitalize="none"
                className="w-100"
                value={formState.email || ''}
                onChange={handleFormFieldChange}
                newDesignSystemStyles
                placeholder="jane@highpeaksfarm.com"
              />
            </ErrorWrapper>
          </label>
        </div>

        <div className="mb3">
          <label htmlFor="password">
            <p className="secondary font-12 mb2">Password</p>
            <ErrorWrapper
              errors={formErrors.password}
              isShown={didFormSubmissionFail}
            >
              <PasswordInput
                name="password"
                autoCapitalize="none"
                value={formState.password || ''}
                onChange={handleFormFieldChange}
                placeholder="6+ characters"
              />
            </ErrorWrapper>
          </label>
        </div>
        {selectedCountry.allows_phone_number && (
          <div className="mb6">
            <label htmlFor="phone_number">
              <p className="secondary font-12 mb2">Phone number</p>
              <TelephoneInput
                displayErrors={didFormSubmissionFail}
                fullWidth
                inputClassName="w-100"
                name="phone_number"
                placeholder="e.g. 555-555-5555"
              />
            </label>
          </div>
        )}

        <div className="mb3">
          <Button
            variant="primary"
            data-test-id="signup_submit"
            type="submit"
            disabled={isFormSubmitting || !isEmpty(formErrors)}
            className={classnames(buttonClassName, {
              'button--loading': isFormSubmitting
            })}
          >
            {buttonText}
          </Button>
        </div>

        <div className="mb6">
          {isBreeder && (
            <p className="f2 mid-gray tc pb4">
              By entering your mobile number and clicking &lsquo;Continue&rsquo;
              you agree to receive occasional automated promotional and
              service-related text messages from Good Dog at the number
              provided. You also consent to placing and receiving calls from
              other Good Dog members routed through Good Dog&apos;s call relay
              service, which may display a unique Good Dog telephone number.
              Messages and calls may be sent or connected using automated
              systems. Consent is not a condition of any purchase or platform
              access. Reply STOP to opt out of promotional messages at any time.
              Message and data rates may apply.
            </p>
          )}
          <p className="f2 mid-gray tc">
            By continuing, I agree to Good Dog&apos;s&nbsp;
            {isBreeder && (
              <Fragment>
                <a className="underline" href="/breeder-code-of-ethics">
                  Breeder Code of Ethics
                </a>
                ,&nbsp;
              </Fragment>
            )}
            <a className="underline" href={TOS_PATH}>
              Terms of Service
            </a>
            &nbsp;and{' '}
            <a className="underline" href={PRIVACY_PATH}>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </div>
  );
}

SignupForm.propTypes = {
  formState: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone_number: PropTypes.string,
    password: PropTypes.string,
    program_name: PropTypes.string,
    country_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    phone_number_type: PropTypes.string
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  formErrors: PropTypes.object.isRequired,
  didFormSubmissionFail: PropTypes.bool.isRequired,
  handleFormFieldChange: PropTypes.func.isRequired,
  handleFormSubmission: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool.isRequired,
  userType: PropTypes.oneOf(Object.values(USER_TYPES)).isRequired,
  onAuth: PropTypes.func,
  buttonText: PropTypes.string,
  buttonClassName: PropTypes.string,
  referredBy: PropTypes.string,
  countriesData: PropTypes.shape({
    value: PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string
        })
      ).isRequired
    }).isRequired
  }).isRequired,
  selectedCountry: PropTypes.shape({
    allows_phone_number: PropTypes.bool
  }),
  partnership: PropTypes.string
};

SignupForm.defaultProps = {
  onAuth: null,
  buttonText: 'Continue',
  buttonClassName: 'w-100 pv3 font-16',
  referredBy: null,
  selectedCountry: {},
  partnership: null
};
